import { useEffect, useRef } from 'react';
import { MdCall } from 'react-icons/md';
import { CallToolController } from './callPartInterface';
import { CallToolViewProps } from './callPartProps';
import { HiOutlinePhone } from 'react-icons/hi';
import { ChatControllerBaseConfig } from '../../base';

function formatDuration(durationMs: number): string {
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes === 0) {
        return `${seconds}s`;
    } else if (minutes < 60) {
        return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    } else {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        if (remainingMinutes === 0) {
            return `${hours}h`;
        } else {
            return `${hours}h ${remainingMinutes}m`;
        }
    }
}

export function createCallToolController(
    config: ChatControllerBaseConfig & {
        timeoutMs: number;
        autoAccept: boolean;
    }
): CallToolController {
    return {
        useProps(_context, item, props): CallToolViewProps {
            const timeoutRef = useRef<NodeJS.Timeout | null>(null);
            const acceptedRef = useRef(false);

            // Clear timeout on accept or reject
            const handleAccept = () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                props.onAccept();
            };

            const handleReject = () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                props.onReject();
            };

            // Auto-timeout logic for pending calls
            useEffect(() => {
                // Only set timeout for pending calls (no result and not rejected)
                if (
                    !item.tool.result &&
                    !item.tool.args.auto_accept &&
                    !props.disclosure.isOpen
                ) {
                    console.log('Setting up auto-reject timeout for call');
                    timeoutRef.current = setTimeout(() => {
                        console.log('Call auto-rejected after 10 seconds');
                        props.onTimeout();
                    }, config.timeoutMs); // 10 seconds
                }

                // Cleanup function - clear timeout when effect reruns or component unmounts
                return () => {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                    }
                };
            }, [item.tool.result, item.tool.state, props.onTimeout]);

            useEffect(() => {
                if (
                    (item.tool.args.auto_accept || config.autoAccept) &&
                    !acceptedRef.current
                ) {
                    acceptedRef.current = true;
                    handleAccept();
                }
            }, [item.tool.args.auto_accept, handleAccept]);

            if (item.tool.result?.status.kind === 'finished') {
                return {
                    status: {
                        kind: 'ended',
                        Icon: MdCall,
                        duration: {
                            label: `Call ended after ${formatDuration(item.tool.result.status.duration_ms)}`,
                        },
                    },
                };
            }
            if (item.tool.result?.status.kind === 'rejected') {
                return {
                    status: {
                        kind: 'rejected',
                        Icon: HiOutlinePhone,
                        description: `Call rejected`,
                    },
                };
            }
            if (item.tool.result?.status.kind === 'timeout') {
                return {
                    status: {
                        kind: 'timeout',
                        Icon: HiOutlinePhone,
                        description: `Call timed out`,
                    },
                };
            }
            item.tool;
            return {
                status: {
                    kind: 'pending',
                    caller: {
                        label: item.definition.display_name,
                    },
                    description: `is calling you`,
                    accept: {
                        label: 'Accept',
                        tooltip: {
                            label: 'Accept call',
                        },
                        button: {
                            onClick: handleAccept,
                        },
                    },
                    reject: {
                        label: 'Reject',
                        tooltip: {
                            label: 'Reject call',
                        },
                        button: {
                            onClick: handleReject,
                        },
                    },
                    call: {
                        item: {
                            integration: item.instance,
                            tool: item.tool,
                            thread: {
                                id: item.message.thread,
                            },
                        },
                        isOpen: props.disclosure.isOpen,
                        onClose: props.disclosure.onClose,
                        onEnd: props.onEnd,
                    },
                },
            };
        },
    };
}
